<template>
  <div class="edit-user-container">
    <b-loading
      :is-full-page="true"
      :active.sync="isLoading"
      :can-cancel="false"
    ></b-loading>
    <template v-if="user">
      <b-notification
        v-if="user.isPendingApproval"
        class="pending-approval-notification"
        type="is-info"
        :closable="false"
      >
        {{ $t('edit_user_profile.pending_approval_message') }}
      </b-notification>
      <b-notification
        v-if="user.isRejected"
        class="pending-approval-notification"
        type="is-info"
        :closable="false"
      >
        {{ $t('edit_user_profile.rejected_message') }}
      </b-notification>

      <h4>{{ $t('edit_user_profile.title') }}</h4>

      <div class="profile-header">
        <div class="profile-avatar">
          <img class="avatar" src="/img/icons/user.png" />
        </div>
        <div class="profile-actions">
          <template
            v-if="(user.isPendingApproval || user.isRejected) && canApproveUser"
          >
            <b-button @click="handleApproveUser" type="is-success">{{
              $t('edit_user_profile.approve_button')
            }}</b-button>
            <b-button
              v-if="!user.isRejected"
              @click="handleRejectUser"
              type="is-danger"
              >{{ $t('edit_user_profile.reject_button') }}</b-button
            >
          </template>
          <template v-else-if="canEditUser">
            <b-button @click="deleteUser" type="is-danger">{{
              $t('edit_user_profile.delete_button')
            }}</b-button>
            <b-button @click="updateUserProfile" type="is-primary">{{
              $t('edit_user_profile.update_button')
            }}</b-button>
          </template>
          <b-button @click="handleClose">{{
            $t('edit_user_profile.cancel_button')
          }}</b-button>
        </div>
      </div>

      <div class="edit-user-form-container">
        <div class="column">
          <b-field :label="$t('edit_user_profile.full_name')">
            <b-input v-model="user.fullname" :disabled="!canEditUser"></b-input>
          </b-field>
          <b-field :label="$t('edit_user_profile.email')">
            <b-input v-model="user._id" :disabled="!canEditUser"></b-input>
          </b-field>
          <b-field :label="$t('edit_user_profile.primary_location')">
            <b-select
              v-model="user.location"
              :placeholder="userLocationName"
              @input="updateLocation"
              :disabled="!canEditUser"
            >
              <option v-for="site in sites" :value="site._id" :key="site._id">
                {{ site.siteName }}
              </option>
            </b-select>
          </b-field>
          <b-field :label="$t('edit_user_profile.secondary_locations')">
            <b-taginput
              v-model="user.secondaryLocations"
              :data="filteredSites"
              field="siteName"
              autocomplete
              :disabled="!canEditUser"
              :allow-new="false"
              :open-on-focus="true"
              @typing="getFilteredSitesTags"
            >
            </b-taginput>
          </b-field>
          <b-field :label="$t('edit_user_profile.phone_number')">
            <b-input v-model="user.phone" :disabled="!canEditUser"></b-input>
          </b-field>
        </div>

        <div class="column">
          <b-field :label="$t('edit_user_profile.role')">
            <b-select
              v-model="newUserRole"
              :placeholder="displayRoleLabel"
              :disabled="!canEditRoleSelection"
            >
              <option v-if="canAssignPowerAdminRole" :value="'power'">
                {{ $t('edit_user_profile.power_admin_role') }}
              </option>
              <option v-for="role in roles" :value="role._id" :key="role._id">
                {{ role.rolename }}
              </option>
            </b-select>
          </b-field>

          <label class="label">{{ $t('edit_user_profile.ownership') }}</label>
          <div class="ownership">
            <b-loading
              :is-full-page="false"
              :active="ownershipLoading"
            ></b-loading>
            <div v-if="!ownershipLoading">
              <p class="ownership-help">
                Transfer ownership of this user's templates and jobs to another
                user.
              </p>
              <p>
                {{
                  $t('edit_user_profile.user_owned_template_count', {
                    count: userOwnedTemplatesCount,
                  })
                }}
              </p>
              <p>
                {{
                  $t('edit_user_profile.user_owned_job_count', {
                    count: userOwnedJobsCount,
                  })
                }}
              </p>

              <b-button
                @click="goToOwnershipReassignPage"
                type="is-primary"
                :disabled="!hasOwnedItems || !canEditUser"
              >
                Reassign Ownership (Templates/Jobs)
              </b-button>
            </div>
            <div v-else class="ownership-loading">
              <p>Loading ownership details...</p>
            </div>
          </div>

          <!-- Roles & Permissions moved to a dedicated panel below for clarity -->
        </div>
      </div>

      <div
        class="permissions-panel"
        :class="{ collapsed: !permissionsExpanded }"
      >
        <div
          class="permissions-header clickable"
          @click="togglePermissionsPanel"
        >
          <div class="permissions-toggle">
            <b-icon
              :icon="permissionsExpanded ? 'chevron-up' : 'chevron-down'"
            ></b-icon>
          </div>
          <div>
            <h5>Permission</h5>
            <p class="permissions-help">
              Role preset permissions are always applied. You can enable
              additional user permissions here.
            </p>
          </div>
        </div>

        <transition name="fade">
          <div v-show="permissionsExpanded">
            <div class="modes-grid">
              <div class="mode" v-for="mode in modes" :key="mode.key">
                <div class="mode-header">
                  <h6>{{ mode.label }}</h6>
                </div>
                <template v-if="mode.sections">
                  <div
                    class="mode-section"
                    v-for="section in mode.sections"
                    v-show="
                      !(
                        section.title === 'Power Admin' &&
                        !canViewPowerAdminSection
                      )
                    "
                    :key="section.title"
                  >
                    <div class="section-title">{{ section.title }}</div>
                    <div class="section-items">
                      <b-checkbox
                        v-for="item in section.items"
                        :key="item.key"
                        v-model="permissionSelections[item.key]"
                        :disabled="
                          !canEditUser || isRolePresetPermission(item.key)
                        "
                        @input="onItemToggle(mode.key)"
                      >
                        {{ item.label }}
                      </b-checkbox>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="mode-items">
                    <b-checkbox
                      v-for="item in mode.items"
                      :key="item.key"
                      v-model="permissionSelections[item.key]"
                      :disabled="
                        !canEditUser || isRolePresetPermission(item.key)
                      "
                      @input="onItemToggle(mode.key)"
                    >
                      {{ item.label }}
                    </b-checkbox>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </template>
  </div>
</template>

<script>
import PERMISSION_MODES from '../../config/permissionsConfig';

export default {
  name: 'edit-user-profile',
  data() {
    return {
      newUserRole: null,
      userLocationName: '',
      privil: false,
      selectedUser: null,
      isLoading: true,
      user: null,
      filteredSites: null,
      ownershipLoading: true,
      permissionChecks: {
        admin: false,
        editor: false,
        publisher: false,
        inspector: false,
        report: false,
      },
      rolePresetPermissions: [],
      // granular permission selections keyed by permission key
      permissionSelections: {},
      // modes imported from centralized config
      modes: PERMISSION_MODES,
      // collapsed by default
      permissionsExpanded: false,
    };
  },
  computed: {
    sites() {
      return this.$store.state.sites;
    },
    sitesWithoutPrimaryLocation() {
      const sitesWithoutPrimaryLocation = [...this.sites];
      return sitesWithoutPrimaryLocation.filter(
        (site) => site._id !== this.user.location
      );
    },
    roles() {
      if (this.$store.getters.isPowerAdmin) {
        return this.$store.state.userRoles;
      }
      return this.$store.state.userRoles.filter(
        (role) => role.rolename.toLowerCase() !== 'power admin'
      );
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    userOwnedTemplatesCount() {
      return this.$store.state.userOwnedTemplatesCount;
    },
    userOwnedJobsCount() {
      return this.$store.state.userOwnedJobsCount;
    },
    hasOwnedItems() {
      return this.userOwnedTemplatesCount > 0 || this.userOwnedJobsCount > 0;
    },
    hasAdditionalPermissions() {
      if (!this.user) return false;
      if (this.isEditingPowerAdminUser) return false;

      return this.modes.some((mode) => {
        const items = mode.sections
          ? mode.sections.flatMap((section) => section.items)
          : mode.items;

        return (
          items &&
          items.some(
            (item) =>
              this.permissionSelections[item.key] &&
              !this.rolePresetPermissions.includes(item.key)
          )
        );
      });
    },
    displayRoleLabel() {
      if (!this.user) {
        return this.$t('edit_user_profile.placeholder_role');
      }

      if (
        this.newUserRole === 'power' ||
        parseInt(this.user.privil, 10) === 1
      ) {
        return this.getPowerRoleLabel();
      }

      if (this.newUserRole) {
        const selectedRole = this.roles.find(
          (role) => role._id === this.newUserRole
        );
        if (selectedRole) {
          return this.getRoleLabel(selectedRole.rolename);
        }
      }

      if (this.user.role) {
        return this.getRoleLabel(this.user.role);
      }

      return this.$t('edit_user_profile.placeholder_role');
    },
    canViewPowerAdminSection() {
      return Boolean(this.$store.getters.isPowerAdmin);
    },
    canApproveUser() {
      return this.$store.getters.hasAnyPermission([
        'admin.approvals.approve_edit_primary',
        'admin.approvals.approve_edit_all',
      ]);
    },
    canEditUser() {
      return this.$store.getters.hasAnyPermission([
        'admin.approvals.approve_edit_primary',
        'admin.approvals.approve_edit_all',
      ]);
    },
    isEditingPowerAdminUser() {
      const privilFlag = parseInt(this.user?.privil, 10) === 1;
      const roleName = String(this.user?.role || '').toLowerCase();
      return privilFlag || roleName === 'power admin';
    },
    canChangeUserRole() {
      return this.canEditUser;
    },
    canEditRoleSelection() {
      // Power admins can always change roles
      if (this.$store.getters.isPowerAdmin) {
        return true;
      }
      // Others with permission can change roles except for power admin users
      return this.canChangeUserRole && !this.isEditingPowerAdminUser;
    },
    canAssignPowerAdminRole() {
      return Boolean(this.$store.getters.isPowerAdmin);
    },
    userLocationNames() {
      const secondaryLocations = (this.user.secondaryLocations || []).map(
        (location) => location._id
      );
      return [this.user.location, ...secondaryLocations].map(
        (location) => this.sites.find((site) => site._id === location).siteName
      );
    },
  },

  watch: {
    newUserRole(newValue, oldValue) {
      if (!this.canEditRoleSelection) {
        this.$nextTick(() => {
          this.newUserRole = oldValue;
        });
        return;
      }

      if (newValue === 'power' && !this.canAssignPowerAdminRole) {
        this.$nextTick(() => {
          this.newUserRole = oldValue;
        });
        return;
      }

      let chosenRole = null;

      if (newValue === 'power') {
        this.user.role = 'admin';
        this.user.privil = 1;
        chosenRole = this.roles.find(
          (role) => role.rolename.toLowerCase() === 'power admin'
        );

        // Power Admin gets all existing permissions
        const allPermissions = this.getAllPermissionKeys();

        this.user.secondaryPermissions = {
          permissions: [],
          toollist: [],
        };
        this.user.permissions = [];
        this.user.toollist = [];

        this.rolePresetPermissions = [...allPermissions];
        this.applyPermissionSelections(allPermissions);
        this.user.permissions = [...allPermissions];
        return;
      }

      chosenRole = this.roles.find((role) => role._id === newValue);
      if (!chosenRole) {
        return;
      }

      this.user.role = chosenRole.rolename;
      this.user.privil = 0;

      const rolePresetPermissions = this.getRolePresetPermissions(chosenRole);

      // Role change resets user-level overrides to enforce selected preset.
      this.user.secondaryPermissions = {
        permissions: [],
        toollist: [],
      };
      this.user.permissions = [];
      this.user.toollist = [];

      this.rolePresetPermissions = [...rolePresetPermissions];
      this.applyPermissionSelections(rolePresetPermissions);
      this.user.permissions = [...rolePresetPermissions];
    },
  },

  beforeRouteEnter(to, {}, next) {
    next();
  },

  async created() {
    await this.initializeUserProfile();
  },

  async beforeRouteUpdate(to, from, next) {
    if (to.params.id === from.params.id) {
      next();
      return;
    }

    await this.initializeUserProfile(to.params.id, to.params?.user);
    next();
  },

  methods: {
    async initializeUserProfile(routeId, selectedUser) {
      this.isLoading = true;
      this.ownershipLoading = true;
      const id = routeId || this.$route.params.id;
      const selected = selectedUser || this.$route.params?.user;

      try {
        if (selected && selected._id === id) {
          this.$store.commit('SET_USER', selected);
        } else {
          await this.$store.dispatch('getUserProfile', id);
        }

        const requests = [
          this.$store.dispatch('getUserRoles'),
          this.$store.dispatch('countUserOwnedTemplates', id),
          this.$store.dispatch('countUserOwnedJobs', id),
        ];

        if (this.$store.state.sites.length === 0) {
          requests.push(this.$store.dispatch('getSites'));
        }

        await Promise.all(requests);

        this.user = { ...this.$store.state.user };
        if (this.user.secondaryLocations) {
          this.user.secondaryLocations = this.user.secondaryLocations
            .map((locationEntry) => {
              if (locationEntry && typeof locationEntry === 'object') {
                return locationEntry;
              }
              return this.sites.find((site) => site._id === locationEntry);
            })
            .filter(Boolean);
        }

        if (this.currentUser) {
          this.privil = Boolean(parseInt(this.currentUser.privil, 10));
        }

        // Always refresh displayed permissions from the current role preset.
        try {
          const matchingRole = this.roles.find(
            (r) => r.rolename === this.user.role || r._id === this.user.role
          );

          const rolePresetPermissions =
            this.getRolePresetPermissions(matchingRole);
          const additionalPermissions = this.getUserAdditionalPermissions(
            this.user
          );

          const permissionsToApply =
            rolePresetPermissions.length > 0
              ? Array.from(
                  new Set([...rolePresetPermissions, ...additionalPermissions])
                )
              : this.normalizeList(this.user.permissions);

          this.rolePresetPermissions = [...rolePresetPermissions];
          this.applyPermissionSelections(permissionsToApply);
          this.user.permissions = [...permissionsToApply];
        } catch (err) {
          // ignore initialization errors and leave defaults
          console.warn('Failed to init permission checks', err);
        }

        this.updateLocation();
        this.filteredSites = this.sitesWithoutPrimaryLocation;
      } finally {
        this.ownershipLoading = false;
        this.isLoading = false;
      }
    },

    updateLocation() {
      if (this.user.secondaryLocations) {
        this.user.secondaryLocations = this.user.secondaryLocations.filter(
          (location) => location._id !== this.user.location
        );
      }
      const site = this.sites.find(
        (siteToFind) => siteToFind._id === this.user.location
      );
      this.userLocationName = site ? site.locationName : '';
    },

    handleClose() {
      this.$router.push({
        name: 'admin-root',
      });
    },

    goToOwnershipReassignPage() {
      this.$router.push({
        name: 'edit-user-jobownership',
        params: { id: this.user._id },
      });
    },

    async refreshAdminUsersLists() {
      const requests = [
        this.$store.dispatch('UsersModule/GET_USERS'),
        this.$store.dispatch('UsersModule/GET_USERS_PENDING_APPROVAL'),
        this.$store.dispatch('UsersModule/GET_USERS_LOGGED_IN_TODAY'),
        this.$store.dispatch('UsersModule/GET_USERS_WITHOUT_SEPIA_INFO'),
      ];

      const isCurrentUserPowerAdmin = this.$store.getters.isPowerAdmin;

      if (isCurrentUserPowerAdmin) {
        requests.push(this.$store.dispatch('UsersModule/GET_USERS_REJECTED'));
      }

      await Promise.all(requests);
    },

    async updateUserProfile() {
      const userToUpdate = { ...this.user };
      if (userToUpdate.secondaryLocations) {
        userToUpdate.secondaryLocations = this.user.secondaryLocations.map(
          (location) => location._id
        );
      }

      const rolePresetPermissions = this.getCurrentRolePresetPermissions();
      const selectedPermissions = [];
      this.modes.forEach((mode) => {
        const items = mode.sections
          ? mode.sections.flatMap((section) => section.items)
          : mode.items;

        items.forEach((item) => {
          if (this.permissionSelections[item.key]) {
            selectedPermissions.push(item.key);
          }
        });
      });

      const additionalPermissions = selectedPermissions.filter(
        (permission) => !rolePresetPermissions.includes(permission)
      );

      const additionalTools = this.modes
        .filter((mode) =>
          (mode.sections
            ? mode.sections.flatMap((section) => section.items)
            : mode.items
          ).some(
            (item) =>
              additionalPermissions.includes(item.key) &&
              this.permissionSelections[item.key]
          )
        )
        .map((mode) => mode.key);

      userToUpdate.secondaryPermissions = {
        permissions: additionalPermissions,
        toollist: additionalTools,
      };

      // Keep legacy fields in sync for compatibility with older readers.
      userToUpdate.permissions = additionalPermissions;
      userToUpdate.toollist = additionalTools;

      try {
        await this.$store.dispatch('updateUser', userToUpdate);

        this.$store.dispatch(
          'setSuccessMessage',
          this.$t('edit_user_profile.update_success_message')
        );

        this.$router.push({
          name: 'admin-root',
        });
      } catch (error) {
        const errorMessage =
          error.message || this.$t('edit_user_profile.update_error_message');
        this.$buefy.toast.open({
          message: errorMessage,
          type: 'is-danger',
          duration: 5000,
        });
      }
    },

    async deleteUser() {
      this.$buefy.dialog.confirm({
        message: 'Are you sure you want to delete this user?',
        type: 'is-danger',
        hasIcon: true,
        icon: 'delete',
        confirmText: 'Delete',
        onConfirm: async () => {
          this.isLoading = true;
          await this.$store.dispatch('deleteUser', this.user);

          this.$store.dispatch(
            'setSuccessMessage',
            this.$t('edit_user_profile.delete_success_message')
          );

          setTimeout(() => {
            this.$router.push({
              name: 'admin-root',
            });
          }, 3000);
        },
      });
    },

    async handleApproveUser() {
      const name = this.user.fullname;
      const { role } = this.user;
      const locations = this.userLocationNames;
      this.$buefy.dialog.confirm({
        message: this.$t('edit_user_profile.approve_question', {
          name,
          role,
          locations,
        }),
        type: 'is-success',
        hasIcon: true,
        icon: 'check-decagram',
        confirmText: this.$t('edit_user_profile.approve_button'),
        onConfirm: async () => {
          this.isLoading = true;

          const userToUpdate = { ...this.user };
          if (userToUpdate.secondaryLocations) {
            userToUpdate.secondaryLocations = this.user.secondaryLocations.map(
              (location) => location._id
            );
          }

          await this.$store.dispatch('approveUser', userToUpdate);
          await this.refreshAdminUsersLists();

          this.$store.dispatch(
            'setSuccessMessage',
            this.$t('edit_user_profile.approve_success_message')
          );

          setTimeout(() => {
            this.$router.push({
              name: 'admin-root',
            });
          }, 3000);
        },
      });
    },

    async handleRejectUser() {
      const name = this.user.fullname;
      this.$buefy.dialog.confirm({
        message: this.$t('edit_user_profile.reject_question', { name }),
        type: 'is-danger',
        hasIcon: true,
        icon: 'delete',
        confirmText: this.$t('edit_user_profile.reject_button'),
        onConfirm: async () => {
          this.isLoading = true;
          await this.$store.dispatch('rejectUser', this.user);
          await this.refreshAdminUsersLists();

          this.$store.dispatch(
            'setSuccessMessage',
            this.$t('edit_user_profile.reject_success_message')
          );

          setTimeout(() => {
            this.$router.push({
              name: 'admin-root',
            });
          }, 3000);
        },
      });
    },

    toggleMode(modeKey, value) {
      const mode = this.modes.find((m) => m.key === modeKey);
      if (!mode) return;

      const nextValue = Boolean(value);
      const items = mode.sections
        ? mode.sections.flatMap((section) => section.items)
        : mode.items;

      items.forEach((item) => {
        if (this.isRolePresetPermission(item.key)) {
          this.$set(this.permissionSelections, item.key, true);
          return;
        }

        this.$set(this.permissionSelections, item.key, nextValue);
      });

      const any = items.some((item) => this.permissionSelections[item.key]);
      this.$set(this.permissionChecks, modeKey, any);
    },

    onItemToggle(modeKey) {
      const mode = this.modes.find((m) => m.key === modeKey);
      if (!mode) return;

      const items = mode.sections
        ? mode.sections.flatMap((section) => section.items)
        : mode.items;

      const any = items.some((item) => this.permissionSelections[item.key]);
      this.$set(this.permissionChecks, modeKey, any);
    },

    togglePermissionsPanel() {
      this.permissionsExpanded = !this.permissionsExpanded;
    },

    normalizeList(list) {
      if (!list) return [];
      if (Array.isArray(list)) return list.map((item) => String(item).trim());
      if (typeof list === 'string') {
        return list
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return [];
    },

    isScopedPermissionKey(value) {
      // Permission keys are namespaced ("report.engineering_review").
      // This excludes tool names or legacy values that are not permission keys.
      return String(value || '').includes('.');
    },

    getRoleLabel(roleName) {
      const normalizedRole = String(roleName || '').trim();
      if (!normalizedRole) {
        return this.$t('edit_user_profile.placeholder_role');
      }

      return this.hasAdditionalPermissions
        ? `${normalizedRole} (*)`
        : normalizedRole;
    },

    getPowerRoleLabel() {
      return this.getRoleLabel(this.$t('edit_user_profile.power_admin_role'));
    },

    getRoleOptionLabel(role) {
      const roleName = role?.rolename || '';
      const isSelectedRole = this.newUserRole === role?._id;
      const isCurrentRole = !this.newUserRole && this.user?.role === roleName;

      if (isSelectedRole || isCurrentRole) {
        return this.getRoleLabel(roleName);
      }

      return roleName;
    },

    getAllPermissionKeys() {
      const keys = [];
      this.modes.forEach((mode) => {
        const items = mode.sections
          ? mode.sections.flatMap((section) => section.items)
          : mode.items;
        items.forEach((item) => keys.push(item.key));
      });
      return keys;
    },

    getRolePresetPermissions(role) {
      const rolePermissions = this.normalizeList(role?.permissions);
      if (rolePermissions.length > 0) {
        return rolePermissions;
      }

      const roleTools = this.normalizeList(role?.toollist);
      const permissions = [];
      this.modes.forEach((mode) => {
        const items = mode.sections
          ? mode.sections.flatMap((section) => section.items)
          : mode.items;
        items.forEach((item) => {
          if (roleTools.includes(item.key)) {
            permissions.push(item.key);
          }
        });
      });

      return Array.from(new Set(permissions));
    },

    getCurrentRolePresetPermissions() {
      let selectedRole = null;

      if (this.newUserRole === 'power') {
        selectedRole = this.roles.find(
          (role) => role.rolename.toLowerCase() === 'power admin'
        );
      } else if (this.newUserRole) {
        selectedRole = this.roles.find((role) => role._id === this.newUserRole);
      }

      if (!selectedRole) {
        selectedRole = this.roles.find(
          (role) =>
            role.rolename === this.user?.role || role._id === this.user?.role
        );
      }

      return this.getRolePresetPermissions(selectedRole);
    },

    getUserAdditionalPermissions(userData = this.user) {
      const secondaryPermissions =
        userData?.secondaryPermissions || userData?.secondaryPermission;

      if (secondaryPermissions && typeof secondaryPermissions === 'object') {
        const fromSecondary = this.normalizeList(
          secondaryPermissions.permissions
        )
          .concat(this.normalizeList(secondaryPermissions.toollist))
          .filter((permission) => this.isScopedPermissionKey(permission));

        if (fromSecondary.length > 0) {
          return Array.from(new Set(fromSecondary));
        }
      }

      const normalizedPermissions = this.normalizeList(userData?.permissions);
      return normalizedPermissions.filter((permissionKey) =>
        this.isScopedPermissionKey(permissionKey)
      );
    },

    isRolePresetPermission(permissionKey) {
      return this.rolePresetPermissions.includes(permissionKey);
    },

    applyPermissionSelections(permissions) {
      const normalizedPermissions = this.normalizeList(permissions);
      const selections = {};
      const modeSelected = {};

      this.modes.forEach((mode) => {
        let any = false;

        const items = mode.sections
          ? mode.sections.flatMap((section) => section.items)
          : mode.items;

        items.forEach((item) => {
          const isSelected = normalizedPermissions.includes(item.key);
          selections[item.key] = isSelected;
          if (isSelected) {
            any = true;
          }
        });

        modeSelected[mode.key] = any;
      });

      this.permissionSelections = selections;
      this.permissionChecks = {
        ...this.permissionChecks,
        ...modeSelected,
      };
    },

    getFilteredSitesTags(text) {
      this.filteredSites = this.sitesWithoutPrimaryLocation.filter(
        (location) => {
          return (
            location.siteName
              .toString()
              .toLowerCase()
              .indexOf(text.toLowerCase()) >= 0
          );
        }
      );
    },
  },
};
</script>

<style lang="scss">
.img-space {
  margin-top: 142px;
}
.avatar {
  width: 80px;
  height: 80px;
  margin: 30px 0;
  margin-left: 8px;
  border-radius: 80px;
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  background-color: rgba(37, 48, 92, 0.3);
}

.profile-header {
  width: 75%;
  margin: 8px auto 18px auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.profile-avatar .avatar {
  width: 64px;
  height: 64px;
  margin: 0;
}
.profile-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.edit-user-container {
  h4 {
    margin-left: 5%;
    margin-top: 2%;
    font-weight: bold;
    color: #333333;
    font-size: 20px;
  }
}

.edit-user-form-container {
  display: flex;
  flex-direction: row;
  width: 75%;
  margin: auto;
}

.first-column {
  display: flex;
  width: 50%;
  flex-direction: column;
}

.column {
  display: flex;
  flex-direction: column;
}

.row {
  display: flex;
  flex-direction: row;
}
select,
.select,
.is-empty {
  width: 100%;
}

.actions {
  display: flex;
  gap: 10px;
  right: 0;
  bottom: 0;
  position: absolute;
  margin-right: 10%;
  margin-bottom: 5%;
}

.ownership {
  position: relative;
  background-color: rgba(255, 255, 255, 1);

  .ownership-help {
    margin-bottom: 8px;
  }

  .ownership-empty {
    margin-top: 8px;
  }
}

.notification.pending-approval-notification {
  padding-top: 10px;
  padding-bottom: 10px;
  margin: 0;
}

.modes-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
.mode-items {
  display: flex;
  flex-direction: column;
}
.mode-items > * {
  margin-bottom: 5px;
}
.mode-section {
  margin-top: 10px;
  margin-bottom: 12px;
}
.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 6px;
  padding-left: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.section-items {
  display: flex;
  flex-direction: column;
  padding-left: 8px;
}
.section-items > * {
  margin-bottom: 5px;
}
.mode h6 {
  margin: 0 0 6px 0;
  font-weight: 600;
}

.permissions-panel {
  margin-top: 18px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #eef0f3;
  border-radius: 6px;
}
.permissions-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}
.permissions-help {
  margin: 4px 0 0 0;
  color: #666;
  font-size: 13px;
}
.mode {
  padding: 10px;
  border-radius: 4px;
  background: #fafafa;
  border: 1px solid #f2f3f5;
}
.mode-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.mode-select-all {
  font-size: 13px;
}

.permissions-panel {
  width: 75%;
  margin: 18px auto;
  padding: 12px 18px;
  background: #ffffff;
  border: 1px solid #eef0f3;
  border-radius: 6px;
}
.permissions-panel.collapsed {
  padding-bottom: 8px;
}
.permissions-header.clickable {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}
.permissions-toggle {
  flex-shrink: 0;
  color: #555;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
